/**
 * ChainVault WP - Built-in Wallet Utility
 *
 * @package ChainVault
 * @since   1.0.0
 */

import { ethers }  from 'ethers';
import { Keypair } from '@solana/web3.js';

const STORAGE_KEY = 'chainvault_wallet';

export function validateSeedPhrase( mnemonic ) {
    const words = mnemonic.trim().toLowerCase().split( /\s+/ );
    return words.length === 12 && words.every( w => w.length >= 2 );
}

async function phraseToBytes( phrase ) {
    const normalized = phrase.trim().toLowerCase();
    const encoded    = new TextEncoder().encode( normalized );
    const hashBuffer = await crypto.subtle.digest( 'SHA-256', encoded );
    return new Uint8Array( hashBuffer );
}

export async function deriveEthWallet( mnemonic ) {
    try {
        const seedBytes  = await phraseToBytes( mnemonic );
        const seedHex    = Array.from( seedBytes ).map( b => b.toString( 16 ).padStart( 2, '0' ) ).join( '' );
        const privateKey = '0x' + seedHex;
        const wallet     = new ethers.Wallet( privateKey );
        return { address: wallet.address, privateKey: wallet.privateKey };
    } catch ( error ) {
        throw new Error( 'Failed to derive ETH wallet: ' + error.message );
    }
}

export async function deriveSolWallet( mnemonic ) {
    try {
        const solBytes = await phraseToBytes( mnemonic + ' solana' );
        const keypair  = Keypair.fromSeed( solBytes );
        return { address: keypair.publicKey.toBase58(), secretKey: Array.from( keypair.secretKey ) };
    } catch ( error ) {
        throw new Error( 'Failed to derive SOL wallet: ' + error.message );
    }
}

export async function deriveBtcWallet( mnemonic ) {
    try {
        const btcBytes   = await phraseToBytes( mnemonic + ' bitcoin' );
        const btcHex     = Array.from( btcBytes ).map( b => b.toString( 16 ).padStart( 2, '0' ) ).join( '' );
        const privateKey = '0x' + btcHex;
        const wallet     = new ethers.Wallet( privateKey );
        const btcAddress = '1' + wallet.address.slice( 2, 34 );
        return { address: btcAddress, privateKey: privateKey };
    } catch ( error ) {
        throw new Error( 'Failed to derive BTC wallet: ' + error.message );
    }
}

export async function createWalletFromSeed( mnemonic ) {
    try {
        const ethWallet = await deriveEthWallet( mnemonic );
        const solWallet = await deriveSolWallet( mnemonic );
        const btcWallet = await deriveBtcWallet( mnemonic );
        return { mnemonic, ethereum: ethWallet, solana: solWallet, bitcoin: btcWallet, created: Date.now() };
    } catch ( error ) {
        throw error;
    }
}

export function saveWalletToStorage( walletData ) {
    try {
        const json    = JSON.stringify( walletData );
        const encoded = btoa( encodeURIComponent( json ) );
        localStorage.setItem( STORAGE_KEY, encoded );
        return true;
    } catch ( error ) {
        return false;
    }
}

export function loadWalletFromStorage() {
    try {
        const encoded = localStorage.getItem( STORAGE_KEY );
        if ( ! encoded ) return null;
        const json = decodeURIComponent( atob( encoded ) );
        return JSON.parse( json );
    } catch ( error ) {
        return null;
    }
}

export function walletExistsInStorage() {
    return !! localStorage.getItem( STORAGE_KEY );
}

export function deleteWalletFromStorage() {
    localStorage.removeItem( STORAGE_KEY );
}

export async function sendEthTransaction( privateKey, toAddress, amount, rpcUrl ) {
    try {
        const provider = new ethers.JsonRpcProvider( rpcUrl );
        const wallet   = new ethers.Wallet( privateKey, provider );
        const tx       = await wallet.sendTransaction( {
            to:    toAddress,
            value: ethers.parseEther( amount.toString() ),
        } );
        await tx.wait();
        return tx.hash;
    } catch ( error ) {
        throw error;
    }
}

export async function sendSolTransaction( secretKey, toAddress, amount, rpcUrl ) {
    try {
        const {
            Connection,
            PublicKey,
            Transaction,
            SystemProgram,
            LAMPORTS_PER_SOL,
            sendAndConfirmTransaction,
        } = await import( '@solana/web3.js' );

        const endpoints = [
            'https://rpc.ankr.com/solana',
            'https://solana-mainnet.g.alchemy.com/v2/demo',
            'https://api.mainnet-beta.solana.com',
            rpcUrl,
        ].filter( Boolean );

        let lastError;

        for ( const endpoint of endpoints ) {
            try {
                const connection  = new Connection( endpoint, 'confirmed' );
                const fromKeypair = Keypair.fromSecretKey( new Uint8Array( secretKey ) );
                const toPubkey    = new PublicKey( toAddress );

                const balance  = await connection.getBalance( fromKeypair.publicKey );
                const lamports = Math.floor( amount * LAMPORTS_PER_SOL );

                if ( balance < lamports + 5000 ) {
                    throw new Error(
                        'Insufficient balance. You have ' + ( balance / LAMPORTS_PER_SOL ).toFixed( 6 ) + ' SOL but need ' + amount + ' SOL plus fees.'
                    );
                }

                const transaction = new Transaction().add(
                    SystemProgram.transfer( {
                        fromPubkey: fromKeypair.publicKey,
                        toPubkey,
                        lamports,
                    } )
                );

                const signature = await sendAndConfirmTransaction( connection, transaction, [ fromKeypair ] );
                return signature;

            } catch ( err ) {
                if ( err.message && err.message.includes( 'Insufficient' ) ) {
                    throw err;
                }
                lastError = err;
                continue;
            }
        }

        throw lastError || new Error( 'All Solana RPC endpoints failed' );

    } catch ( error ) {
        throw error;
    }
}

export function parseSeedPhrase( mnemonic ) {
    return mnemonic.trim().split( ' ' ).filter( Boolean );
}
